import {
  ScheduleData,
  validateScheduleData,
  OlympicsEvent,
} from '../validation/scheduleDataSchemas';
import { getOlympicYearFromURL } from '../utils/urlUtils';

// Data is loaded from the same server using relative path
// Frontend: s3://pakastin-dev/olympics/index.html
// Data: s3://pakastin-dev/olympics/merged-data.json
// REACT_APP_BASE_URL can be set to full URL (e.g., https://pakastin.dev.datadesk.hs.fi/olympics)
// Falls back to PUBLIC_URL which handles the homepage setting from package.json

/**
 * Gets the test file parameter from URL
 * Returns 'test1', 'test2', or null if not specified
 */
function getTestFileParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  const testFile = params.get('testfile');

  if (testFile === 'test1' || testFile === 'test2') {
    return testFile;
  }

  return null;
}

/**
 * Determines which data file to use based on URL parameters
 *
 * Priority:
 * 1. If testfile param exists → merged-data-2022-{testfile}.json
 * 2. If year is 2022 → merged-data-2022.json
 * 3. Otherwise → merged-data.json
 *
 * Examples:
 * - ?testfile=test1 → merged-data-2022-test1.json
 * - ?testfile=test2 → merged-data-2022-test2.json
 * - ?date=2022-02-05 → merged-data-2022.json
 * - no params → merged-data.json
 */
function getDataUrl(): string {
  const testFile = getTestFileParam();
  const year = getOlympicYearFromURL();

  let filename: string;

  if (testFile) {
    // Test files are always for 2022
    filename = `merged-data-2022-${testFile}.json`;
  } else if (year === 2022) {
    filename = 'merged-data-2022.json';
  } else {
    filename = 'merged-data.json';
  }
  // PUBLIC_URL is defined by setting "homepage" in package.json, so
  // use it as fallback since REACT_APP_BASE_URL might not be set at all
  return `${process.env.REACT_APP_BASE_URL || process.env.PUBLIC_URL}/${filename}`;
}

/**
 * Creates a manual opening ceremony event
 * This event is always shown with "notstarted" status and appears in both views
 */
function createOpeningCeremonyEvent(): OlympicsEvent {
  return {
    id: 9999999, // Unique ID that won't clash with real events
    name: 'Avajaiset, San Siron stadion, Milano',
    statusType: 'notstarted',
    eventParticipants: [],
    date: '2026-02-06 21:00:00',
    hasFin: false, // Not counted as Finnish event, but shown in Suomalaiset view via special logic
    tv: 'TV2, Eurosport1',
    medal: false,
  };
}

/**
 * Adds the opening ceremony event to the schedule data
 * The event is inserted in chronological order based on date
 */
function addOpeningCeremony(data: ScheduleData): ScheduleData {
  const openingCeremony = createOpeningCeremonyEvent();

  // Find the correct position to insert the event (chronologically)
  const insertIndex = data.findIndex((event) => {
    return new Date(event.date) > new Date(openingCeremony.date);
  });

  // If no later event found, add at the end, otherwise insert at the found position
  if (insertIndex === -1) {
    return [...data, openingCeremony];
  } else {
    return [...data.slice(0, insertIndex), openingCeremony, ...data.slice(insertIndex)];
  }
}

/**
 * Fetches and validates schedule data from JSON file
 *
 * Data flow:
 * 1. Fetch raw JSON from S3
 * 2. Validate structure with Zod schemas
 * 3. Return validated ScheduleData
 *
 * @returns Validated schedule data
 * @throws Error if fetch fails or validation fails
 */
export async function fetchScheduleData(): Promise<ScheduleData> {
  const dataUrl = getDataUrl();
  const response = await fetch(dataUrl);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const rawJson = await response.json();

  const validation = validateScheduleData(rawJson);
  if (!validation.success) {
    console.error('Data validation errors:', validation.errors);
    throw new Error(`Invalid data format: ${validation.errors?.join(', ') || 'Unknown error'}`);
  }

  // Add opening ceremony event to the data
  const dataWithOpeningCeremony = addOpeningCeremony(validation.data);

  return dataWithOpeningCeremony;
}
